import React, { CSSProperties, useContext, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import gsap from 'gsap';
import { useDialogFocus } from 'hooks/useDialogFocus';
import { useIsPhoneTouch } from 'hooks/useIsPhoneTouch';
import { Promotion } from 'reducers/playerPromos';
import { safeAreaModalBackdropPadding } from 'utils/safeArea';

import { BottomSheet } from 'components/BottomSheet';
import ForcePortrait from 'components/ForcePortrait/ForcePortrait';

import { buildPromoDisplay } from './promoDisplay';
import { getPromoModalCopy } from './promoMessaging';
import { getPromoVisualTheme, getTierProgressFill } from './promoTheme';

type PromoEntryModalProps = {
  promo: Promotion;
  onClose: () => void;
  onViewDetails: () => void;
  onDontShowAgain?: () => void;
  /**
   * How the modal is presented. `auto` (default) opens a bottom drawer on a phone and a
   * centered modal on tablet and desktop (EN-11291 drawer rule). `drawer` and `dialog`
   * force one or the other, for previews.
   */
  presentation?: 'auto' | 'drawer' | 'dialog';
};

const PromoEntryModal = ({
  promo,
  onClose,
  onViewDetails,
  onDontShowAgain,
  presentation = 'auto'
}: PromoEntryModalProps) => {
  const isPhone = useIsPhoneTouch();
  const asDrawer = presentation === 'drawer' || (presentation === 'auto' && isPhone);
  const fillRef = useRef<HTMLDivElement>(null);
  const animatedPromoRef = useRef<string | number | null>(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsId = useId();
  const titleId = useId();
  // Only the lobby is portrait-locked on a landscape phone; over the multi-table grid (or a
  // table) the prompt keeps the device's orientation, as LobbyOverlayLock does.
  const { activeView } = useContext(ViewManagerContext);
  const overPortraitLobby = activeView === 'lobby';
  const dialogRef = useRef<HTMLDivElement>(null);
  const display = buildPromoDisplay(promo);
  const theme = getPromoVisualTheme(display.format, promo.player_facing_config?.theme);
  const promoKey = promo.promo_id || promo.promo_name;
  const remaining = Math.max(display.targetValue - display.progressValue, 0);
  const remainingLabel =
    display.remainingLabel ||
    (display.format === 'weekly_timeback_credit'
      ? `$${remaining.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
      : `${Number.isInteger(remaining) ? remaining.toFixed(0) : remaining.toFixed(1)}h`);
  const copy = getPromoModalCopy({
    promoName: promo.promo_name,
    remainingLabel,
    display,
    playerFacingConfig: promo.player_facing_config
  });
  const progressCardTheme = getEntryProgressCardStyles(theme);
  const hasRemainingToken = copy.remainingMessage.includes(remainingLabel);
  const [beforeRemaining, afterRemaining = ''] = copy.remainingMessage.split(remainingLabel);
  // One status per row: the second slot only appears when it adds something (a next tier,
  // or a reward label that differs from the status). Placeholders like "Not qualified"
  // would just repeat the left side.
  const secondaryStatus = display.tierState
    ? display.nextTierLabel || ''
    : isEarnedPlaceholder(display.earnedLabel) || display.earnedLabel === copy.statusLabel
    ? ''
    : display.earnedLabel;
  const handleDismiss = () => {
    if (dontShowAgain) {
      onDontShowAgain?.();
    }
    onClose();
  };
  // Centered modal keyboard behaviour (the drawer's comes from BottomSheet): focus moves
  // in, Tab stays inside, Escape dismisses, focus returns on close.
  useDialogFocus(dialogRef, handleDismiss, !asDrawer);
  const handleViewDetails = () => {
    if (dontShowAgain) {
      onDontShowAgain?.();
    }
    onViewDetails();
  };

  useEffect(() => {
    if (!fillRef.current) return;

    const width = `${display.progressPct}%`;
    if (animatedPromoRef.current === promoKey) {
      gsap.set(fillRef.current, { width });
      return;
    }

    animatedPromoRef.current = promoKey;
    gsap.fromTo(fillRef.current, { width: '0%' }, { width, duration: 0.8, ease: 'power2.out' });
  }, [display.progressPct, promoKey]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, []);

  // Rotate WITH the portrait-locked lobby on mobile-landscape (this portals to
  // <body>, so the rotation lives inside the portal).
  const content = (
    <>
      <style>{entryModalAnimationCss}</style>
      <div style={entryHeaderStyle}>
        <div style={entryHeaderCopyStyle}>
          <div id={titleId} style={entryTitleStyle}>
            {copy.title}
          </div>
        </div>
        <button aria-label='Close' style={entryCloseStyle} onClick={handleDismiss}>
          <CloseIcon style={closeIconStyle} />
        </button>
      </div>

      <div
        data-testid='promo-entry-progress-card'
        style={{
          ...entryProgressCardStyle,
          ...progressCardTheme.card
        }}>
        {progressCardTheme.frame && (
          <span
            aria-hidden='true'
            data-testid='promo-entry-progress-card-frame'
            style={progressCardTheme.frame}
          />
        )}
        <div style={{ ...entryProgressMetaStyle, ...progressCardTheme.text }}>
          <span>{display.metricLabel}</span>
          <strong style={entryProgressValueStyle}>{display.progressLabel}</strong>
        </div>
        <div style={{ ...entryProgressTrackStyle, backgroundColor: theme.trackColor }}>
          <div
            ref={fillRef}
            style={{
              ...entryProgressFillStyle,
              background: getTierProgressFill(theme, display.tierState)
            }}
          />
        </div>
        <div style={{ ...entryProgressFootStyle, ...progressCardTheme.text }}>
          <span>{copy.statusLabel}</span>
          {secondaryStatus && <span>{secondaryStatus}</span>}
        </div>
      </div>

      <div data-testid='promo-entry-message' style={entryMessageStyle}>
        {hasRemainingToken ? (
          <>
            {beforeRemaining}
            <span style={entryHighlightStyle}>{remainingLabel}</span>
            {afterRemaining}
          </>
        ) : (
          copy.remainingMessage
        )}
      </div>

      {copy.helperMessage && <div style={entrySubMessageStyle}>{copy.helperMessage}</div>}

      {/* Play leads (primary, left); the details action follows. The config fields keep
          their meaning: primaryAction labels details, secondaryAction labels play. */}
      <div style={entryActionRowStyle}>
        <button
          className='promo-entry-action'
          style={entryPrimaryButtonStyle}
          onClick={handleDismiss}>
          {copy.secondaryAction}
        </button>
        <button
          className='promo-entry-action'
          style={entrySecondaryButtonStyle}
          onClick={handleViewDetails}>
          {copy.primaryAction}
        </button>
      </div>

      <div style={entryUtilityRowStyle}>
        <label style={entryDontShowStyle}>
          <input
            className='promo-entry-dont-show-input'
            checked={dontShowAgain}
            onChange={(event) => setDontShowAgain(event.target.checked)}
            style={entryDontShowInputStyle}
            type='checkbox'
          />
          <span
            className='promo-entry-dont-show-box'
            style={{
              ...entryDontShowBoxStyle,
              ...(dontShowAgain ? entryDontShowBoxCheckedStyle : {})
            }}
            aria-hidden='true'>
            {dontShowAgain ? '✓' : ''}
          </span>
          <span>Don't show me this again</span>
        </label>
        <button
          type='button'
          className='promo-entry-details-toggle'
          style={entryDetailsToggleStyle}
          aria-expanded={detailsOpen}
          aria-controls={detailsId}
          onClick={() => setDetailsOpen((open) => !open)}>
          {detailsOpen ? 'Hide promotional details' : 'Show promotional details'}
          <ExpandMoreIcon className='promo-entry-details-chevron' style={entryChevronStyle} />
        </button>
      </div>

      {/* Collapsed by default; grid-rows 0fr -> 1fr animates to the content's own height. */}
      <div className='promo-entry-details' data-open={detailsOpen}>
        <div
          id={detailsId}
          className='promo-entry-details-inner'
          aria-hidden={!detailsOpen}
          {...(detailsOpen ? {} : ({ inert: '' } as Record<string, string>))}>
          <div style={entryFooterStyle}>{copy.footer}</div>
        </div>
      </div>
    </>
  );

  // Phone: a bottom drawer (swipe down, tap outside or close to dismiss). It is always as
  // tall as its content: opening the promotional details grows it, hiding them shrinks it
  // back, and only content taller than the screen scrolls (demo 2026-10-28).
  if (asDrawer) {
    return (
      <BottomSheet
        open
        onClose={handleDismiss}
        testId='promo-entry-backdrop'
        maxWidth={560}
        ariaLabelledBy={titleId}
        forcePortrait={overPortraitLobby}>
        <div
          className='promo-entry-drawer'
          data-testid='promo-entry-drawer'
          style={entryDrawerContentStyle}
          onTouchStart={keepScrollFromDragging}
          onTouchMove={keepScrollFromDragging}>
          {content}
        </div>
      </BottomSheet>
    );
  }

  // Tablet and desktop: the centered modal.
  return createPortal(
    <ForcePortrait enabled={overPortraitLobby}>
      <div data-testid='promo-entry-backdrop' style={modalBackdropStyle} onClick={handleDismiss}>
        <div
          ref={dialogRef}
          role='dialog'
          aria-modal='true'
          aria-labelledby={titleId}
          tabIndex={-1}
          style={{ ...entryModalStyle }}
          onClick={(event) => event.stopPropagation()}>
          <span
            className='promo-entry-edge-glint promo-entry-edge-glint-top'
            style={entryEdgeGlintStyle}
            aria-hidden='true'
          />
          <span
            className='promo-entry-sparkle promo-entry-sparkle-one'
            style={entrySparkleStyle}
            aria-hidden='true'
          />
          <span
            className='promo-entry-sparkle promo-entry-sparkle-two'
            style={entrySparkleStyle}
            aria-hidden='true'
          />
          {content}
        </div>
      </div>
    </ForcePortrait>,
    document.body
  );
};

export default PromoEntryModal;

/** Inside the drawer, a touch on content that is already scrolled scrolls it; only a
 *  touch at the top drags the drawer down. */
const keepScrollFromDragging = (event: React.TouchEvent<HTMLDivElement>) => {
  if (event.currentTarget.scrollTop > 0) event.stopPropagation();
};

const EARNED_PLACEHOLDERS = new Set(['', 'not qualified', 'no timeback yet']);

const isEarnedPlaceholder = (label: string | undefined): boolean =>
  EARNED_PLACEHOLDERS.has((label || '').trim().toLowerCase());

const getEntryProgressCardStyles = (
  theme: ReturnType<typeof getPromoVisualTheme>
): {
  card: CSSProperties;
  frame?: CSSProperties;
  text: CSSProperties;
} => ({
  card: {
    background: theme.background,
    backgroundImage: theme.background,
    borderColor: theme.borderColor,
    boxShadow: theme.boxShadow,
    color: theme.iconColor
  },
  frame: theme.detailsCardFrameStyle,
  text: {
    color: theme.iconColor
  }
});

const entryModalAnimationCss = `
  /* @import must come before every other rule, or browsers drop it and the fonts never load. */
  @import url('https://fonts.googleapis.com/css2?family=Rye&family=Special+Elite&family=Oswald:wght@400;500;600;700;800&display=swap');

  /* The real checkbox is invisible; its keyboard focus shows on the drawn box. */
  .promo-entry-dont-show-input:focus-visible + .promo-entry-dont-show-box {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  .promo-entry-edge-glint {
    position: absolute;
    top: 0;
    left: 18px;
    width: min(112px, 28%);
    height: 2px;
    border-radius: 999px;
    opacity: 0;
    pointer-events: none;
    z-index: 1;
    animation: promoEntryGlintTop 5.8s ease-in-out infinite;
  }

  .promo-entry-sparkle {
    position: absolute;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    opacity: 0;
    pointer-events: none;
    z-index: 1;
  }

  .promo-entry-sparkle-one {
    top: 20px;
    right: 22%;
    animation: promoEntrySparkle 6.2s ease-in-out 1.2s infinite;
  }

  .promo-entry-sparkle-two {
    bottom: 18px;
    left: 19%;
    animation: promoEntrySparkle 7s ease-in-out 3s infinite;
  }

  @keyframes promoEntryGlintTop {
    0%, 44%, 100% {
      opacity: 0;
      transform: translateX(0);
    }
    12% {
      opacity: 0.9;
    }
    30% {
      opacity: 0.75;
      transform: translateX(calc(min(520px, 100vw - 36px) - 148px));
    }
    40% {
      opacity: 0;
      transform: translateX(calc(min(520px, 100vw - 36px) - 148px));
    }
  }

  @keyframes promoEntrySparkle {
    0%, 64%, 100% {
      opacity: 0;
      transform: scale(0.45);
    }
    72% {
      opacity: 0.85;
      transform: scale(1);
    }
    80% {
      opacity: 0.2;
      transform: scale(0.72);
    }
  }

  .promo-entry-action {
    transition: transform 120ms cubic-bezier(0.2, 0, 0, 1);
  }

  .promo-entry-action:active {
    transform: scale(0.96);
  }

  .promo-entry-action:focus-visible,
  .promo-entry-details-toggle:focus-visible {
    outline: 2px solid #f4a340;
    outline-offset: 2px;
  }

  .promo-entry-details-toggle:hover {
    color: #fff4d3;
  }

  .promo-entry-details-chevron {
    transition: transform 200ms cubic-bezier(0.2, 0, 0, 1);
  }

  .promo-entry-details-toggle[aria-expanded='true'] .promo-entry-details-chevron {
    transform: rotate(180deg);
  }

  .promo-entry-details {
    display: grid;
    grid-template-rows: 0fr;
    margin-top: -16px;
    transition: grid-template-rows 240ms cubic-bezier(0.2, 0, 0, 1);
  }

  .promo-entry-details[data-open='true'] {
    grid-template-rows: 1fr;
  }

  .promo-entry-details-inner {
    min-height: 0;
    overflow: hidden;
  }

  .promo-entry-details-inner > div {
    padding-top: 14px;
  }

  .promo-entry-drawer {
    scrollbar-width: none;
  }

  .promo-entry-drawer::-webkit-scrollbar {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .promo-entry-edge-glint,
    .promo-entry-sparkle {
      animation: none;
      opacity: 0;
    }

    .promo-entry-action,
    .promo-entry-details,
    .promo-entry-details-chevron {
      transition: none;
    }
  }
`;

const modalBackdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1300,
  backgroundColor: 'rgba(0, 0, 0, 0.64)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  // Base gutter + safe-area insets so the top-aligned card's close button never
  // tucks under the notch / status-bar clock (no-op on web — insets are 0).
  ...safeAreaModalBackdropPadding('18px'),
  WebkitOverflowScrolling: 'touch'
};

const entryModalStyle: CSSProperties = {
  position: 'relative',
  outline: 'none',
  width: 'min(520px, 100%)',
  backgroundColor: '#111827',
  backgroundImage:
    'radial-gradient(ellipse 520px 360px at 80% 0%, rgba(232,130,31,0.08), transparent 60%), linear-gradient(180deg, #182338 0%, #0b1424 100%)',
  border: '1px solid rgba(232, 130, 31, 0.42)',
  borderRadius: 18,
  boxShadow: '0 24px 80px rgba(0,0,0,0.56), inset 0 1px 0 rgba(255,220,160,0.1)',
  color: '#fff',
  margin: 'auto 0',
  overflow: 'hidden',
  padding: '24px 24px 28px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
};

const entryHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px'
};

const entryHeaderCopyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  minWidth: 0
};

const entryCloseStyle: CSSProperties = {
  width: 38,
  height: 38,
  border: '1px solid rgba(232,130,31,0.38)',
  borderRadius: 8,
  backgroundColor: 'rgba(255,255,255,0.06)',
  color: '#f1d3a1',
  cursor: 'pointer',
  lineHeight: 1,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0
};

const closeIconStyle: CSSProperties = {
  fontSize: '1rem'
};

/* All caps reads as a headline at a glance (demo 2026-10-26); the copy stays in natural
   case and the transform does the work, balanced so no line is left with one word. */
const entryTitleStyle: CSSProperties = {
  color: '#fff4d3',
  fontFamily: "'Oswald', sans-serif",
  fontSize: 'clamp(1.6rem, 4vw, 2rem)',
  fontWeight: 800,
  letterSpacing: '0.03em',
  lineHeight: 1.08,
  textTransform: 'uppercase',
  textWrap: 'balance' as CSSProperties['textWrap'],
  textShadow: '0 2px 0 rgba(0,0,0,0.45)'
};

const entryProgressCardStyle: CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 12,
  // Tighter on a phone, the desktop 18/20 from about 500 px up (demo 2026-10-28).
  padding: 'clamp(12px, 3.2vw, 18px) clamp(14px, 4vw, 20px)',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  position: 'relative'
};

const entryProgressMetaStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  color: 'rgba(255,255,255,0.58)',
  // Oswald, as the lobby strip: the typewriter face was hard to read on the green
  // (demo 2026-10-28). The label stays lighter than the hours it names.
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.95rem',
  fontWeight: 500,
  letterSpacing: '0.02em',
  lineHeight: 1.2,
  fontVariantNumeric: 'tabular-nums',
  position: 'relative',
  zIndex: 3
};

const entryProgressValueStyle: CSSProperties = {
  fontSize: '1.15rem',
  fontWeight: 700,
  whiteSpace: 'nowrap'
};

const entryProgressTrackStyle: CSSProperties = {
  height: 8,
  borderRadius: 5,
  backgroundColor: 'rgba(255,255,255,0.1)',
  overflow: 'hidden',
  position: 'relative',
  zIndex: 3
};

const entryProgressFillStyle: CSSProperties = {
  height: '100%',
  borderRadius: 5,
  backgroundColor: '#E98C26'
};

const entryProgressFootStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  color: 'rgba(255,255,255,0.7)',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.8rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  lineHeight: 1.2,
  position: 'relative',
  textTransform: 'uppercase',
  zIndex: 3
};

const entryMessageStyle: CSSProperties = {
  color: '#fff4d3',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '1.05rem',
  fontWeight: 700,
  lineHeight: 1.35
};

const entryHighlightStyle: CSSProperties = {
  color: '#f4a340'
};

const entrySubMessageStyle: CSSProperties = {
  color: '#f1d3a1',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.9rem',
  fontWeight: 600,
  lineHeight: 1.45
};

const entryActionRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '10px'
};

const entryPrimaryButtonStyle: CSSProperties = {
  border: '1px solid rgba(92,42,10,0.5)',
  borderRadius: 8,
  background: 'linear-gradient(180deg, #f3a04a 0%, #e07a1c 54%, #b85913 100%)',
  boxShadow: 'inset 0 1px 0 rgba(255,220,160,0.5), 0 6px 14px rgba(0,0,0,0.35)',
  color: '#1a0e06',
  cursor: 'pointer',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.9rem',
  fontWeight: 800,
  letterSpacing: '0.08em',
  padding: '14px 16px',
  textTransform: 'uppercase'
};

const entrySecondaryButtonStyle: CSSProperties = {
  ...entryPrimaryButtonStyle,
  borderColor: 'rgba(232,130,31,0.46)',
  background: 'linear-gradient(180deg, #321a12 0%, #21100b 100%)',
  boxShadow: 'inset 0 1px 0 rgba(255,220,160,0.08)',
  color: '#f1d3a1'
};

const entryFooterStyle: CSSProperties = {
  color: 'rgba(231,217,181,0.62)',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.62rem',
  lineHeight: 1.55
};

const entryDontShowStyle: CSSProperties = {
  alignItems: 'center',
  color: '#f1d3a1',
  cursor: 'pointer',
  display: 'inline-flex',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.66rem',
  gap: 10,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  userSelect: 'none'
};

const entryDrawerContentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  // At most the screen under the status bar, less the sheet's handle and bottom inset. The
  // sheet reports the rotated height when the portrait-locked lobby is turned on its side.
  maxHeight:
    'calc(var(--sheet-viewport-height, 100dvh) - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 48px)',
  overflowX: 'hidden',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  margin: '-0.5rem 0 0',
  // Holds the details toggle's 12 px hit-area overhang, so a drawer that fits its content
  // never scrolls by those 12 px.
  paddingBottom: 12
};

const entryUtilityRowStyle: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px 16px',
  justifyContent: 'space-between'
};

const entryDetailsToggleStyle: CSSProperties = {
  alignItems: 'center',
  background: 'none',
  border: 'none',
  color: '#f1d3a1',
  cursor: 'pointer',
  display: 'inline-flex',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.66rem',
  gap: 4,
  letterSpacing: '0.08em',
  // Invisible hit area to ~44px tall without moving the text.
  // Vertical only, so the hit area never pushes the drawer's scroll box sideways.
  margin: '-12px 0',
  minHeight: 44,
  padding: '12px 0',
  textDecoration: 'underline',
  textTransform: 'uppercase',
  textUnderlineOffset: 3,
  transition: 'color 150ms ease-out'
};

const entryChevronStyle: CSSProperties = {
  fontSize: '1.1rem'
};

const entryDontShowInputStyle: CSSProperties = {
  height: 1,
  opacity: 0,
  position: 'absolute',
  width: 1
};

const entryDontShowBoxStyle: CSSProperties = {
  alignItems: 'center',
  border: '1px solid rgba(232,130,31,0.7)',
  borderRadius: 4,
  color: '#1a0e06',
  display: 'inline-flex',
  flexShrink: 0,
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.76rem',
  fontWeight: 800,
  height: 20,
  justifyContent: 'center',
  width: 20
};

const entryDontShowBoxCheckedStyle: CSSProperties = {
  background: 'linear-gradient(180deg, #f3a04a 0%, #e07a1c 100%)',
  boxShadow: '0 0 10px rgba(232,130,31,0.35)'
};

const entryEdgeGlintStyle: CSSProperties = {
  background: 'linear-gradient(90deg, transparent, rgba(255, 200, 140, 0.76), transparent)',
  boxShadow: '0 0 10px rgba(255, 200, 140, 0.76)'
};

const entrySparkleStyle: CSSProperties = {
  background: '#ffd089',
  boxShadow: '0 0 8px #ffd089, 0 0 14px rgba(232, 130, 31, 0.38)'
};
